import { useState, useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield } from "lucide-react";
import PageHero from "./PageHero";
import KennelSEO from "./KennelSEO";

const DogCard = ({ dog, idx }) => (
  <Link to={`/psy/${dog.id}`} data-testid={`dog-card-${dog.id}`}>
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08, duration: 0.5 }}>
      <div className="bg-[#1A1A1D] gallery-wow aspect-[3/4]">
        {dog.image_url ? (
          <img src={`${dog.image_url}?v=2`} alt={dog.name} onError={(e) => { e.target.style.display = 'none'; }}
              className="w-full h-full object-cover object-top" />
        ) : (
          <div className="w-full aspect-[3/4] flex items-center justify-center text-[#B8B8B8]/10 text-6xl font-serif">{dog.name[0]}</div>
        )}
        {/* Top-left: health badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1 z-[7]">
          {dog.health?.filter(h => h.status === 'ok').slice(0, 3).map((h, i) => (
            <span key={i} className="text-[7px] px-1.5 py-0.5 bg-[#0B0B0D]/50 backdrop-blur-sm text-[#B8B8B8]/40 border border-[#B8B8B8]/10 tracking-wider">
              {h.name}: {h.result}
            </span>
          ))}
        </div>
        {/* Top-right: FCI badge */}
        <div className="absolute top-3 right-3 z-[7]">
          <span className="text-[7px] px-1.5 py-0.5 bg-[#0B0B0D]/50 backdrop-blur-sm text-[#B8B8B8]/30 border border-[#B8B8B8]/10 tracking-wider">FCI</span>
        </div>
        <div className="wow-overlay" />
        <div className="wow-label">
          <p className="text-[10px] tracking-[0.2em] text-[#D4AF37]/60 mb-1 uppercase">{dog.titles?.split(",")[0]?.trim()}</p>
          <p className="text-[11px] text-[#B8B8B8]/60 font-light tracking-wide mb-2">{dog.registered_name}</p>
          <span className="wow-label-text flex items-center gap-1">ZOBACZ PROFIL <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </motion.div>
  </Link>
);

const KennelDogs = () => {
  const { API } = useOutletContext();
  const [dogs, setDogs] = useState([]);

  useEffect(() => { fetch(`${API}/dogs`).then(r => r.json()).then(setDogs); }, [API]);

  return (
    <div>
      <KennelSEO
        title="Nasze psy hodowlane | Wolfe vom Katzenberg — Owczarek Niemiecki"
        description="Poznaj psy hodowlane Wolfe vom Katzenberg. Owczarki niemieckie z pelnym rodowodem FCI, badaniami HD/ED/DNA i doswiadczeniem wystawowym."
        canonical="https://wolfevomkatzenberg.pl/psy"
      />
      {/* ═══ HEADER ═══ */}
      <PageHero
        breadcrumbs={[{ label: "NASZE PSY" }]}
        label="NASZE PSY"
        title="Poznaj psy, które tworzą naszą hodowlę"
        description="Za każdym z naszych psów stoi charakter, historia i relacja, którą budujemy każdego dnia. To nie tylko osiągnięcia czy rodowody — to przede wszystkim psy, z którymi żyjemy, pracujemy i spędzamy czas. Każdy z nich wnosi coś wyjątkowego do naszej hodowli."
        testId="dogs"
      />

      <div className="px-6 md:px-12 py-16 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {dogs.map((dog, idx) => <DogCard key={dog.id} dog={dog} idx={idx} />)}
        </div>
        {dogs.length === 0 && <p className="text-[#B8B8B8]/30 text-center py-24 text-sm">Ładowanie...</p>}
      </div>

      {/* ═══ OPIS TYTUŁÓW I BADAŃ ═══ */}
      <section className="bg-[#141416] border-t border-[#2A2A2D] px-6 md:px-12 py-20">
        <div className="max-w-5xl mx-auto">
          <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">SŁOWNIK</span>
          <h2 className="font-serif text-[clamp(1.5rem,3vw,2.2rem)] text-[#F5F5F5] mb-4">Tytuły i badania</h2>
          <p className="text-[#B8B8B8]/50 text-sm mb-12 max-w-2xl">Co oznaczają skróty widoczne przy naszych psach? Poniżej znajdziesz wyjaśnienie najważniejszych tytułów, badań zdrowotnych i licencji.</p>
          
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-0">
            {/* Badania zdrowotne */}
            <div className="mb-10">
              <h3 className="text-xs tracking-[0.2em] text-[#D4AF37] mb-6 pb-2 border-b border-[#2A2A2D]">BADANIA ZDROWOTNE</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">HD (Hip Dysplasia)</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Badanie dysplazji stawów biodrowych. Wyniki od A (doskonałe) do E (ciężka dysplazja). HD-A oznacza stawy wolne od dysplazji.</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">ED (Elbow Dysplasia)</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Badanie dysplazji stawów łokciowych. Wynik 0/0 oznacza brak zmian chorobowych w obu stawach.</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">DNA GPR</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Genetyczny profil rodowodowy — test potwierdzający tożsamość i pochodzenie psa na poziomie DNA.</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">DM (Degenerative Myelopathy)</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Test genetyczny na mielopatię zwyrodnieniową. N/N oznacza brak nosicielstwa genu choroby.</p>
                </div>
              </div>
            </div>

            {/* Szkolenia i tytuły */}
            <div className="mb-10">
              <h3 className="text-xs tracking-[0.2em] text-[#D4AF37] mb-6 pb-2 border-b border-[#2A2A2D]">SZKOLENIA I TYTUŁY</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">BH-VT (Begleithundprüfung)</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Egzamin psa towarzyszącego — podstawowy test posłuszeństwa i zachowania w ruchu ulicznym. Warunek do dalszych szkoleń.</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">IGP / IPO (1–3)</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Międzynarodowy egzamin psa użytkowego. Obejmuje ślad, posłuszeństwo i obronę. Poziomy od 1 (podstawowy) do 3 (najwyższy).</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">KKL (Körklasse)</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Klasa kwalifikacyjna — ocena psa pod kątem budowy, charakteru i predyspozycji hodowlanych. KKL1 — szczególnie zalecany do hodowli.</p>
                </div>
              </div>
            </div>

            {/* Licencje hodowlane */}
            <div className="mb-10">
              <h3 className="text-xs tracking-[0.2em] text-[#D4AF37] mb-6 pb-2 border-b border-[#2A2A2D]">LICENCJE HODOWLANE</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">Korung (Licencja hodowlana)</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Najwyższa kwalifikacja hodowlana. Pies musi zdać egzaminy szkoleniowe, badania zdrowotne i ocenę budowy. Potwierdza pełną przydatność do hodowli.</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">Körschein</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Certyfikat kwalifikacji hodowlanej wydawany przez niemiecki SV. Zawiera szczegółowy opis budowy, pomiary i ocenę charakteru.</p>
                </div>
              </div>
            </div>

            {/* Wystawy */}
            <div className="mb-10">
              <h3 className="text-xs tracking-[0.2em] text-[#D4AF37] mb-6 pb-2 border-b border-[#2A2A2D]">OCENY WYSTAWOWE</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">VA / V / SG / G</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Oceny na wystawach: VA (doskonała — wybrana), V (doskonała), SG (bardzo dobra), G (dobra). VA jest najwyższą oceną przyznawaną na BSZS.</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5F5F5] font-medium">CACIB</p>
                  <p className="text-[13px] text-[#B8B8B8]/50 mt-1 leading-relaxed">Certyfikat Przyznany na Międzynarodowej Wystawie pod patronatem FCI. Wymagany do uzyskania tytułu Międzynarodowego Championa Piękności.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KennelDogs;
